import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useTransform, useScroll } from "framer-motion";
import {
  ArrowRight, Banknote, Truck, RotateCcw, ShieldCheck, Leaf, Timer,
  Phone, Mail, MapPin, Send, Store, Star, Quote,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Marquee } from "../components/Marquee";
import { FadeUp, Eyebrow, MaskedLine } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";

const HERO_IMG = "/hero-gama.jpg";

// Pozele implicite ale caruselului hero (folosite când admin nu a setat altele)
const DEFAULT_HERO_SLIDES = [
  "https://images.unsplash.com/photo-1642429948029-19d6dadcea5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxsYXVuZHJ5JTIwZGV0ZXJnZW50JTIwZGV0ZXJnZW50JTIwbGlxdWlkJTIwcG9kaXVtJTIwcHJvZHVjdCUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc5MTM5NzM3MHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1642429948123-bb852afcb0f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxsYXVuZHJ5JTIwZGV0ZXJnZW50JTIwbGlxdWlkJTIwcG9kaXVtJTIwcHJvZHVjdCUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc5MTM5NzM3MHww&ixlib=rb-4.1.0&q=85",
];
const LIFESTYLE_IMG = "https://images.unsplash.com/photo-1622473590925-e3616c0a41bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHx3YXNoaW5nJTIwbWFjaGluZSUyMHNvYXAlMjBidWJibGVzJTIwY2xlYW4lMjBob21lfGVufDB8fHx8MTc5MTM5NzM3N3ww&ixlib=rb-4.1.0&q=85";
const AROMA_NEUTRAL_IMG = "https://images.unsplash.com/photo-1649005011845-ef225c89da86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxkZXRlcmdlbnQlMjBib3R0bGUlMjBwcm9kdWN0JTIwY2xlYW4lMjBmcmVzaCUyMHNwbGFzaHxlbnwwfHx8fDE3OTEzOTczNzB8MA&ixlib=rb-4.1.0&q=85";
const AROMA_FLORAL_IMG = "https://images.unsplash.com/photo-1642429948029-19d6dadcea5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxsYXVuZHJ5JTIwZGV0ZXJnZW50JTIwZGV0ZXJnZW50JTIwbGlxdWlkJTIwcG9kaXVtJTIwcHJvZHVjdCUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc5MTM5NzM3MHww&ixlib=rb-4.1.0&q=85";

const BENTO_SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-8", "md:col-span-6", "md:col-span-6"];

const HERO_FACTS = [
  { icon: Banknote, label: "Plată ramburs la livrare" },
  { icon: Truck, label: "Livrare în 24-48 de ore" },
  { icon: RotateCcw, label: "Retur simplu în 14 zile" },
];

const DEFAULT_AROME = [
  { name: "Ocean Breeze", desc: "Un parfum proaspăt de ocean, discret și curat.", image: AROMA_NEUTRAL_IMG },
  { name: "Floral Fresh", desc: "Un parfum de flori proaspete, care rezistă zile întregi.", image: AROMA_FLORAL_IMG },
];

const DEFAULT_BENEFITS = [
  { title: "Super concentrat", desc: "Un singur bidon de 5L acoperă până la 100 de spălări. Dozezi puțin, speli mult." },
  { title: "Delicat cu pielea", desc: "Formulă blândă, gândită pentru piele sensibilă și pentru întreaga familie." },
  { title: "Preț corect, direct de la producător", "desc": "Fără intermediari: plătești produsul, nu lanțul de distribuție." },
  { title: "Ramburs și retur simplu", desc: "Plătești la livrare și ai 14 zile să te răzgândești, fără întrebări." },
];

const VALUES = [
  { icon: Leaf, title: "Formulă echilibrată", desc: "Dozare clară și ingrediente declarate pe etichetă, conform Reg. CE 648/2004." },
  { icon: ShieldCheck, title: "Calitate verificată", desc: "Fiecare lot este verificat înainte de expediere." },
  { icon: Timer, title: "Livrare în 24-48 de ore", desc: "Comenzile pleacă în aceeași zi lucrătoare." },
];

function splitTitle(t) {
  if (t.includes(", ")) {
    const i = t.indexOf(", ");
    return [t.slice(0, i + 1), t.slice(i + 2)];
  }
  const words = t.split(" ");
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
}

export default function Home() {
  const [content, setContent] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [sampleChoices, setSampleChoices] = useState(["", "", ""]);
  const [sampleBusy, setSampleBusy] = useState(false);
  const [samplePack, setSamplePack] = useState(null);
  const [slide, setSlide] = useState(0);
  const { addItem } = useCart();
  const location = useLocation();

  const lifestyleRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: lifestyleRef, offset: ["start end", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => setContent({}));
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/products").then((r) => setAllProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/reviews/latest").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/sample-pack").then((r) => setSamplePack(r.data)).catch(() => setSamplePack(null));
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 350);
    }
  }, [location.hash, categories]);

  const c = content || {};
  const titleLines = splitTitle(c.hero_title || "Detergent de rufe, fără complicații.");
  const arome = c.arome?.length ? c.arome.map((a, i) => ({ ...a, image: a.image || DEFAULT_AROME[i % DEFAULT_AROME.length].image })) : DEFAULT_AROME;
  const heroImage = c.hero_image || HERO_IMG;
  const aboutImage = c.about_image || LIFESTYLE_IMG;
  const benefits = c.benefits?.length ? c.benefits : DEFAULT_BENEFITS;
  const heroSlides = ((c.hero_slides || []).map((s) => s.image).filter(Boolean).length
    ? (c.hero_slides || []).map((s) => s.image).filter(Boolean)
    : DEFAULT_HERO_SLIDES
  ).filter((src, i, arr) => arr.indexOf(src) === i);

  useEffect(() => {
    if (heroSlides.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 4500);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  // opțiunile de mostre: din „Conținut site" (admin controlează lista și pozele), altfel grupate din catalog
  const sampleOptions = useMemo(() => {
    if ((c.samples || []).length) {
      return c.samples.map((s, i) => ({ id: `s-${i}`, base: s.name, image: s.image || "" }));
    }
    const seen = new Map();
    allProducts.forEach((p) => {
      const base = p.name
        .replace(/\s*\d+(?:[,.]\d+)?\s*L\s*/gi, " ")
        .replace(/\s+/g, " ")
        .replace(/\s+—/g, " —")
        .trim();
      if (base && !seen.has(base)) seen.set(base, { id: p.id, image: p.image });
    });
    return [...seen.entries()].map(([base, v]) => ({ id: v.id, base, image: v.image }));
  }, [allProducts, c.samples]);

  const orderSamples = async () => {
    if (sampleChoices.some((s) => !s) || new Set(sampleChoices).size !== 3) {
      toast.error("Alege 3 mostre diferite înainte de a comanda");
      return;
    }
    setSampleBusy(true);
    try {
      const names = sampleChoices
        .map((id) => sampleOptions.find((o) => o.id === id)?.base)
        .filter(Boolean);
      addItem(samplePack, 1, true, `Mostre alese: ${names.join(", ")}`);
      toast.success("Pachetul de mostre a fost adăugat în coș — plătești doar livrarea de 25 lei");
    } catch {
      toast.error("Pachetul de mostre nu este disponibil momentan");
    } finally {
      setSampleBusy(false);
    }
  };

  const sendContact = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", contactForm);
      toast.success("Mesajul a fost trimis. Revenim curând.");
      setContactForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Mesajul nu a putut fi trimis. Încearcă din nou.");
    }
  };

  return (
    <main data-testid="home-page">
      {/* HERO — text în stânga, carusel compact integrat în dreapta */}
      <section className="mx-auto max-w-[1120px] px-5 pt-8 sm:px-8" data-testid="hero-section">
        <div className="relative overflow-hidden rounded-2xl border border-line bg-[#f0eff7] sm:flex sm:min-h-[440px] sm:items-stretch">
          <div className="relative z-10 flex max-w-xl flex-col justify-center p-8 pb-6 sm:p-12 lg:p-14">
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-base italic text-muted2"
                data-testid="hero-eyebrow"
              >
                {c.hero_eyebrow || "curat, simplu, la îndemână"}
              </motion.p>
              <h1 className="mt-4 font-display text-[32px] font-bold leading-[1.15] tracking-tight text-navy sm:text-5xl" data-testid="hero-headline">
                <MaskedLine delay={0.15}>{titleLines[0]}</MaskedLine>
                <MaskedLine delay={0.3} className="text-sky2">{titleLines[1]}</MaskedLine>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.55 }}
                className="mt-6 max-w-md text-base leading-relaxed text-ink sm:text-[17px]"
                data-testid="hero-subtitle"
              >
                {c.hero_subtitle || "Un singur detergent pentru rufe, cu dozare simplă: 50 ml per spălare. Îl comanzi online și îl plătești la livrare."}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.7 }}
                className="mt-9 flex flex-wrap items-center gap-6"
              >
                <Link
                  to="/produse"
                  className="group flex h-12 items-center gap-2 rounded-lg bg-navy px-8 text-base font-bold text-white transition-colors hover:bg-navy-dark"
                  data-testid="hero-cta-products"
                >
                  {c.hero_cta_primary || "Vezi produsele"}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/produse?categorie=Detergent"
                  className="text-base font-semibold text-navy underline underline-offset-4 transition-colors hover:text-navy-dark"
                  data-testid="hero-cta-howto"
                >
                  {c.hero_cta_secondary || "Cum se folosește"}
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.9 }}
                className="mt-10 flex flex-wrap gap-x-6 gap-y-3"
              >
                {HERO_FACTS.map((f) => (
                  <span key={f.label} className="flex items-center gap-2 text-sm font-semibold text-muted2">
                    <f.icon className="h-4 w-4 text-navy" />
                    {f.label}
                  </span>
                ))}
              </motion.div>
          </div>
          {/* panoul carusel — compact, integrat în dreapta */}
          <div className="relative m-4 mt-0 h-72 overflow-hidden rounded-xl bg-[#eceaf4] sm:m-4 sm:ml-0 sm:h-auto sm:flex-1" data-testid="hero-carousel">
            {heroSlides.map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                loading={i === 0 ? "eager" : "lazy"}
                style={{ filter: "saturate(1.12) contrast(1.03)" }}
                className={`absolute inset-0 h-full w-full object-contain object-center transition-opacity duration-1000 sm:object-cover ${i === slide % heroSlides.length ? "opacity-100" : "opacity-0"}`}
                data-testid={i === 0 ? "hero-image" : undefined}
              />
            ))}
            {heroSlides.length > 1 && (
              <div className="absolute bottom-3 right-3 z-10 flex gap-2" data-testid="hero-carousel-dots">
                {heroSlides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setSlide(i)}
                    aria-label={`Poza ${i + 1} din carusel`}
                    className={`h-2 rounded-full shadow-sm transition-all duration-300 ${i === slide % heroSlides.length ? "w-6 bg-white" : "w-2 bg-white/60 hover:bg-white/90"}`}
                    data-testid={`hero-carousel-dot-${i}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* MARQUEE cu steluțe — stil spumos */}
      <div className="mt-10">
        <Marquee items={c.marquee || []} />
      </div>

      {/* BENTO CATEGORIES */}
      <section id="categorii" className="mx-auto max-w-[1120px] scroll-mt-28 px-5 pb-24 sm:px-8" data-testid="categories-section">
        <FadeUp>
          <Eyebrow>Gama de produse</Eyebrow>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <h2 className="max-w-xl font-display text-2xl font-bold tracking-tight text-navy sm:text-[32px] sm:leading-[1.2]">
              {c.categories_title || "Tot ce ai nevoie pentru curățenie"}
            </h2>
            <Link to="/produse" className="group flex items-center gap-1.5 text-sm font-bold text-navy" data-testid="categories-view-all">
              Toate produsele
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeUp>
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-12">
          {categories.map((cat, i) => (
            <FadeUp key={cat.id} delay={i * 0.08} className={BENTO_SPANS[i % BENTO_SPANS.length]}>
              <Link
                to={`/produse?categorie=${encodeURIComponent(cat.name)}`}
                className="group relative block h-64 overflow-hidden rounded-lg border border-line md:h-72"
                data-testid={`category-tile-${cat.id}`}
              >
                <img
                  src={cat.image}
                  alt={cat.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  {cat.tag && (
                    <span className="mb-3 inline-block rounded-full bg-sky2 px-3 py-1 text-xs font-bold uppercase tracking-[0.08em] text-navy">
                      {cat.tag}
                    </span>
                  )}
                  <h3 className="font-display text-xl font-bold text-white">{cat.name}</h3>
                  <p className="mt-1 max-w-md text-sm text-sky-light">{cat.description}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.08em] text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    Vezi produsele <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* BESTSELLERS */}
      <section className="bg-[#F4F6F8] py-24" data-testid="bestsellers-section">
        <div className="mx-auto max-w-[1120px] px-5 sm:px-8">
          <FadeUp>
            <Eyebrow>Alese de clienți</Eyebrow>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
              <h2 className="max-w-xl font-display text-2xl font-bold tracking-tight text-navy sm:text-[32px] sm:leading-[1.2]">
                {c.bestsellers_title || "Cele mai cumpărate produse"}
              </h2>
              <Link to="/produse" className="group flex items-center gap-1.5 text-sm font-bold text-navy" data-testid="bestsellers-view-all">
                Vezi tot catalogul
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeUp>
          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {featured.slice(0, 4).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* MOSTRE — 3 la alegere, plătești doar livrarea */}
      {samplePack && (
      <section id="mostre" className="mx-auto max-w-[1120px] scroll-mt-28 px-5 py-24 sm:px-8" data-testid="samples-section">
        <FadeUp>
          <div className="grid items-center gap-10 rounded-2xl border border-line bg-skytint/60 p-8 sm:p-12 lg:grid-cols-2">
            <div>
              <span className="inline-block rounded-full bg-navy px-4 py-1.5 text-xs font-bold uppercase tracking-[0.08em] text-white">
                Încearcă înainte
              </span>
              <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-navy sm:text-[32px] sm:leading-[1.2]">
                3 mostre la alegere
              </h2>
              <p className="mt-4 max-w-md text-base leading-relaxed text-ink">
                Primești 3 mostre din gama zilnic — plătești doar livrarea de <strong>25 lei</strong>, ramburs. O singură comandă de mostre per client.
              </p>
              <p className="mt-6 font-display text-3xl font-bold text-navy">0 lei <span className="text-base font-semibold text-muted2">mostrele · 25 lei livrarea</span></p>
            </div>
            <div>
              <div className="grid gap-4 sm:grid-cols-3">
                {[0, 1, 2].map((i) => {
                  const chosen = sampleOptions.find((o) => o.id === sampleChoices[i]);
                  return (
                    <div key={i} className="rounded-xl border-2 border-dashed border-sky2/50 bg-white p-4">
                      {chosen?.image ? (
                        <img src={chosen.image} alt={chosen.base} className="mx-auto h-24 w-24 rounded-lg object-cover" data-testid={`sample-slot-image-${i}`} />
                      ) : (
                        <p className="text-center font-display text-2xl font-bold text-sky2">{i + 1}</p>
                      )}
                      <select
                        value={sampleChoices[i]}
                        onChange={(e) => setSampleChoices((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))}
                        className="mt-3 w-full rounded-lg border border-line bg-white px-2 py-2.5 text-xs font-semibold text-ink outline-none focus:border-sky2"
                        data-testid={`sample-slot-${i}`}
                      >
                        <option value="">Alege mostra</option>
                        {sampleOptions.map((o) => (
                          <option key={o.id} value={o.id}>{o.base}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
              <button
                onClick={orderSamples}
                disabled={sampleBusy}
                className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-navy text-base font-bold text-white transition-colors hover:bg-navy-dark disabled:opacity-60"
                data-testid="samples-order-button"
              >
                {sampleBusy ? "Se adaugă..." : "Alege mostrele"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </FadeUp>
      </section>
      )}

      {/* AROME — tiles în stil spumos */}
      <section className="mx-auto max-w-[1120px] px-5 py-24 sm:px-8" data-testid="arome-section">
        <FadeUp>
          <Eyebrow>Arome</Eyebrow>
          <h2 className="mt-4 max-w-xl font-display text-2xl font-bold tracking-tight text-navy sm:text-[32px] sm:leading-[1.2]">
            Alege aroma potrivită
          </h2>
        </FadeUp>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {arome.map((a, i) => (
            <FadeUp key={a.name} delay={i * 0.12}>
              <div className="group overflow-hidden rounded-lg border border-line bg-white" data-testid={`aroma-tile-${(a.name || "").toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={a.image}
                    alt={`Detergent zilnic ${a.name}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="flex items-center justify-between gap-4 p-6">
                  <div>
                    <h3 className="font-display text-xl font-bold text-navy">{a.name}</h3>
                    <p className="mt-1 text-sm text-muted2">{a.desc}</p>
                  </div>
                  <Link
                    to={`/produse?aroma=${encodeURIComponent(a.name)}`}
                    className="flex h-11 shrink-0 items-center rounded-lg bg-navy px-6 text-sm font-bold text-white transition-colors hover:bg-navy-dark"
                    data-testid={`aroma-cta-${(a.name || "").toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    Vezi produsele
                  </Link>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* DE CE NOI — beneficii numerotate */}
      <section id="de-ce-noi" className="mx-auto max-w-[1120px] scroll-mt-28 px-5 py-24 sm:px-8" data-testid="why-us-section">
        <div className="grid gap-12 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5">
            <Eyebrow>De ce zilnic</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-[32px] sm:leading-[1.2]">
              De ce să ne alegi pe noi
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink">
              Produse de curățenie pentru fiecare zi, făcute simplu și vândute corect — direct de la producător, cu plată ramburs.
            </p>
            <Link to="/produse" className="group mt-7 inline-flex items-center gap-1.5 text-base font-bold text-navy underline underline-offset-4" data-testid="why-us-cta">
              Descoperă produsele
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </FadeUp>
          <div className="lg:col-span-7">
            {benefits.map((b, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <div className={`flex gap-6 py-6 ${i > 0 ? "border-t border-line" : ""}`} data-testid={`why-us-item-${i}`}>
                  <span className="font-display text-2xl font-bold text-sky2">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-display text-xl font-bold text-navy">{b.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted2">{b.desc}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* RECENZII — bandă care rulează automat */}
      {reviews.length > 0 && (
        <section id="recenzii" className="scroll-mt-28 py-24" data-testid="home-reviews-section">
          <div className="mx-auto max-w-[1120px] px-5 sm:px-8">
            <FadeUp>
              <Eyebrow>Recenzii</Eyebrow>
              <h2 className="mt-4 max-w-xl font-display text-2xl font-bold tracking-tight text-navy sm:text-[32px] sm:leading-[1.2]">
                Ce spun clienții
              </h2>
            </FadeUp>
          </div>
          <div className="relative mt-10 overflow-hidden">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-white to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-white to-transparent" />
            <div className="flex w-max animate-marquee gap-6 pr-6 hover:[animation-play-state:paused]" style={{ animationDuration: "70s" }}>
              {[...reviews, ...reviews].map((r, i) => (
                <div
                  key={`${r.id}-${i}`}
                  className="flex w-[320px] shrink-0 flex-col rounded-lg border border-line bg-white p-7"
                  data-testid={i < reviews.length ? `home-review-${r.id}` : undefined}
                >
                  <Quote className="h-6 w-6 text-sky2" />
                  <div className="mt-3 flex items-center gap-0.5 text-amber-400">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} className={`h-4 w-4 ${s < r.rating ? "fill-amber-400" : "fill-line text-line"}`} />
                    ))}
                  </div>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink">{r.comment || "Recomand produsul."}</p>
                  <div className="mt-5 border-t border-line pt-4">
                    <p className="text-sm font-bold text-navy">{r.user_name}</p>
                    <p className="text-xs text-muted2">{r.product_name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DESPRE */}
      <section id="despre" ref={lifestyleRef} className="mx-auto max-w-[1120px] scroll-mt-28 px-5 pb-24 sm:px-8" data-testid="about-section">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="relative lg:col-span-5">
            <div className="overflow-hidden rounded-lg border border-line">
              <motion.img
                src={aboutImage}
                alt="Rufe curate, spălate cu zilnic"
                style={{ y: parallaxY }}
                className="aspect-[4/5] w-full scale-110 object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-3 rounded-lg bg-navy px-6 py-5 text-white sm:-right-6">
              <p className="font-display text-3xl font-bold">20</p>
              <p className="text-xs font-semibold text-sky-light">spălări din fiecare litru</p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <FadeUp>
              <Eyebrow>De ce zilnic</Eyebrow>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-[32px] sm:leading-[1.2]">
                {c.about_title || "Un detergent care spune exact ce face"}
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink sm:text-[17px]">
                {c.about_text || "Fără promisiuni exagerate: dozare clară, compoziție declarată și un preț corect per spălare. Comanzi online, plătești la livrare și știi mereu cât îți ajunge o sticlă."}
              </p>
            </FadeUp>
            <div className="mt-10 grid gap-6 sm:grid-cols-3">
              {VALUES.map((v, i) => (
                <FadeUp key={v.title} delay={i * 0.1}>
                  <div className="h-full rounded-lg border border-line bg-white p-6">
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-skytint">
                      <v.icon className="h-5 w-5 text-navy" />
                    </span>
                    <h3 className="mt-4 font-display text-base font-bold text-navy">{v.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted2">{v.desc}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PENTRU MAGAZINE (B2B) */}
      <section id="pentru-magazine" className="scroll-mt-28 bg-navy py-24" data-testid="b2b-section">
        <div className="mx-auto max-w-[1120px] px-5 sm:px-8">
          <div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
            <FadeUp>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.08em] text-sky-light">
                <Store className="h-4 w-4" />
                Pentru magazine
              </span>
              <h2 className="mt-4 max-w-xl font-display text-2xl font-bold tracking-tight text-white sm:text-[32px] sm:leading-[1.2]">
                {c.b2b_title || "Vinzi detergent în magazinul tău?"}
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-sky-light">
                {c.b2b_text || "Oferim prețuri de raft pentru magazine și distribuitori, cu livrare rapidă din stoc. Cere o ofertă și îți răspundem în aceeași zi lucrătoare."}
              </p>
            </FadeUp>
            <FadeUp delay={0.15}>
              <a
                href={`mailto:${c.contact_email || "contact@detergentzilnic.ro"}?subject=${encodeURIComponent("Cerere ofertă pentru magazine")}`}
                className="inline-flex h-12 items-center gap-2 rounded-lg bg-white px-8 text-base font-bold text-navy transition-colors hover:bg-skytint"
                data-testid="b2b-cta"
              >
                {c.b2b_button || "Cere ofertă"}
                <ArrowRight className="h-4 w-4" />
              </a>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="mx-auto max-w-[1120px] scroll-mt-28 px-5 py-24 sm:px-8" data-testid="contact-section">
        <div className="grid gap-14 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5">
            <Eyebrow>Contact</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-[32px] sm:leading-[1.2]">
              {c.contact_title || "Întrebări despre produse sau comenzi?"}
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink">
              {c.contact_text || "Scrie-ne și îți răspundem în cel mai scurt timp."}
            </p>
            <ul className="mt-8 flex flex-col gap-4 text-sm font-semibold text-ink">
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-skytint"><Phone className="h-4 w-4 text-navy" /></span>
                <a href={`https://wa.me/${(c.contact_phone || "+40 721 000 000").replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-emerald-600 hover:underline" data-testid="contact-phone">{c.contact_phone || "+40 721 000 000"}</a>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-skytint"><Mail className="h-4 w-4 text-navy" /></span>
                <a href={`mailto:${c.contact_email || "contact@detergentzilnic.ro"}`} className="transition-colors hover:text-emerald-600 hover:underline" data-testid="contact-email">{c.contact_email || "contact@detergentzilnic.ro"}</a>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-skytint"><MapPin className="h-4 w-4 text-navy" /></span>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.contact_address || "București, România")}`} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-emerald-600 hover:underline" data-testid="contact-address">{c.contact_address || "București, România"}</a>
              </li>
            </ul>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-7">
            <form onSubmit={sendContact} className="rounded-lg border border-line bg-[#F4F6F8] p-8" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-muted2">Nume</label>
                  <input
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
                    placeholder="Numele tău"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-muted2">Email</label>
                  <input
                    required
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
                    placeholder="email@exemplu.ro"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div className="mt-5">
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-muted2">Mesaj</label>
                <textarea
                  required
                  rows={4}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="w-full resize-none rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
                  placeholder="Cu ce te putem ajuta?"
                  data-testid="contact-message-input"
                />
              </div>
              <button
                type="submit"
                className="mt-6 flex h-12 items-center gap-2 rounded-lg bg-navy px-8 text-base font-bold text-white transition-colors hover:bg-navy-dark"
                data-testid="contact-submit-button"
              >
                Trimite mesajul <Send className="h-4 w-4" />
              </button>
            </form>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
